import { useEffect, useRef, useState } from "react";
import {
  DndContext,
  closestCenter,
  useSensor,
  useSensors,
  PointerSensor,
  KeyboardSensor,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  useSortable,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
  Card,
  CardMedia,
  Typography,
  Box,
  IconButton,
  CircularProgress,
  Modal,
  Tabs,
  Tab,
  TextField,
  FormControlLabel,
  Switch,
  Button,
  Alert,
  AppBar,
  Toolbar,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import axios from "axios";
import { BASE_URL } from "../../components/config/config";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import PanToolIcon from "@mui/icons-material/PanTool";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import UploadIcon from "@mui/icons-material/Upload";
import { useSelector } from "react-redux";

// Permission utility functions
import {
  checkPermission,
  canViewBanner,
  canActivateDeactivateConsumerBanner,
  canDeleteConsumerBanner as canDeleteConsumerBannerUtil,
  canUploadConsumerBanner as canUploadConsumerBannerUtil,
  canViewClientCenterBanner as canViewClientCenterBannerUtil,
  canActivateDeactivateClientCenterBanner as canActivateDeactivateClientCenterBannerUtil,
  canDeleteClientCenterBanner as canDeleteClientCenterBannerUtil,
  canUploadClientCenterBanner as canUploadClientCenterBannerUtil,
  hasFullBannerAccess,
} from "../../utility/permissionUtils";

// Sortable item component
const SortableItem = ({
  _id,
  banner,
  handleImageClick,
  toggleStatus,
  deleteBanner,
  showQR = false,
  canToggle = false,
  canDelete = false,
  canDrag = false,
}) => {
  const { attributes, listeners, setNodeRef, transform, transition } =
    useSortable({ id: _id });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    position: "relative",
  };

  return (
    <Card
      ref={setNodeRef}
      style={style}
      sx={{
        width: 300,
        height: 200,
        display: "flex",
        flexDirection: "column",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        position: "relative",
        borderRadius: "12px",
        overflow: "hidden",
        transition: "transform 0.2s, box-shadow 0.2s",
        "&:hover": {
          transform: "translateY(-4px)",
          boxShadow: "0px 8px 16px rgba(0, 0, 0, 0.2)",
        },
      }}
    >
      {/* Image */}
      <CardMedia
        component="img"
        alt="Banner image"
        image={banner?.image}
        sx={{ width: "100%", height: "100%", objectFit: "cover" }}
        onClick={() => handleImageClick(banner?.image)}
      />

      {/* Active/Inactive Label */}
      {canToggle && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            position: "absolute",
            top: 8,
            right: canDelete ? "45%" : 8,
            gap: "10px",
          }}
        >
          <Box
            sx={{
              backgroundColor: banner?.isActive ? "green" : "red",
              color: "white",
              borderRadius: "20px",
              padding: "4px 8px",
              fontSize: "12px",
              display: "flex",
              alignItems: "center",
              gap: "4px",
              cursor: "pointer",
              userSelect: "none",
              width: "auto",
              justifyContent: "center",
              boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
            }}
            onClick={(e) => {
              e.stopPropagation();
              toggleStatus(_id, !banner.isActive);
            }}
          >
            {banner?.isActive ? (
              <VisibilityIcon fontSize="small" />
            ) : (
              <VisibilityOffIcon fontSize="small" />
            )}
            <Typography variant="body2" sx={{ fontSize: "0.75rem" }}>
              {banner?.isActive ? "Active" : "Inactive"}
            </Typography>
          </Box>
        </Box>
      )}

      {/* Delete Button */}
      {canDelete && (
        <Box
          sx={{
            backgroundColor: "#ff4444",
            p: "4px",
            alignItems: "center",
            borderRadius: "50%",
            justifyContent: "center",
            cursor: "pointer",
            display: "flex",
            width: 28,
            height: 28,
            position: "absolute",
            top: 8,
            right: 8,
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
          }}
          onClick={(e) => {
            e.stopPropagation();
            deleteBanner(_id);
          }}
        >
          <DeleteIcon fontSize="small" htmlColor="#ffffff" />
        </Box>
      )}

      {/* QR ID (if applicable) */}
      {showQR && banner?.qr_id && (
        <Box
          sx={{
            position: "absolute",
            top: 8,
            left: 8,
            backgroundColor: "rgba(0, 0, 0, 0.7)",
            color: "white",
            borderRadius: "4px",
            padding: "2px 6px",
            fontSize: "10px",
          }}
        >
          <Typography variant="body2">{banner.qr_id}</Typography>
        </Box>
      )}

      {/* Sequence Order */}
      <Box
        sx={{
          position: "absolute",
          width: 28,
          height: 28,
          backgroundColor: "rgba(255, 255, 255, 0.9)",
          left: 8,
          bottom: 8,
          color: "black",
          fontWeight: "bold",
          fontSize: "12px",
          textAlign: "center",
          borderRadius: "50%",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
        }}
      >
        <Typography variant="body2">{banner?.sequenceOrder}</Typography>
      </Box>

      {/* Drag Handle */}
      {canDrag && (
        <Box
          {...attributes}
          {...listeners}
          sx={{
            position: "absolute",
            width: 28,
            height: 28,
            backgroundColor: "rgba(255, 255, 255, 0.9)",
            right: 8,
            bottom: 8,
            color: "black",
            fontWeight: "bold",
            fontSize: "12px",
            textAlign: "center",
            borderRadius: "50%",
            padding: "4px",
            cursor: "grab",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.2)",
          }}
        >
          <PanToolIcon fontSize="small" />
        </Box>
      )}
    </Card>
  );
};

// Upload Banner Modal
const UploadBannerModal = ({ open, onClose, onUpload, type, canUpload }) => {
  const [file, setFile] = useState(null);
  const [isActive, setIsActive] = useState(true);
  const [qrId, setQrId] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      if (selectedFile.type.startsWith("image/")) {
        setFile(selectedFile);
        setError("");
      } else {
        setError("Please select a valid image file");
      }
    }
  };

  const handleUpload = async () => {
    if (!file && type !== "consumer") {
      setError("Please select a file");
      return;
    }

    if (type !== "consumer" && !qrId) {
      setError("Please enter a QR ID");
      return;
    }

    setLoading(true);
    try {
      await onUpload(file, qrId, isActive, type);
      onClose();
      setFile(null);
      setQrId("");
      setIsActive(true);
    } catch (err) {
      setError("Failed to upload banner");
    } finally {
      setLoading(false);
    }
  };

  if (!canUpload) {
    return null;
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            Upload {type === "consumer" ? "Consumer" : "Client/Center"} Banner
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent>
        <Box sx={{ mt: 2 }}>
          {type !== "consumer" && (
            <TextField
              fullWidth
              label="QR ID"
              value={qrId}
              onChange={(e) => setQrId(e.target.value)}
              sx={{ mb: 2 }}
              placeholder="Enter INA or INC"
            />
          )}

          <FormControlLabel
            control={
              <Switch
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
                color="primary"
              />
            }
            label="Active"
            sx={{ mb: 2 }}
          />

          <Button
            variant="outlined"
            component="label"
            fullWidth
            startIcon={<UploadIcon />}
            sx={{ mb: 2 }}
          >
            {file ? file.name : "Select Banner Image"}
            <input
              type="file"
              accept="image/png, image/jpeg, image/jpg"
              hidden
              onChange={handleFileChange}
            />
          </Button>

          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={handleUpload}
          variant="contained"
          disabled={loading}
          startIcon={loading ? <CircularProgress size={16} /> : null}
        >
          {loading ? "Uploading..." : "Upload"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const BannerPage = () => {
  const globalState = useSelector((state) => state.AdminReducer);
  const userPermissions = globalState?.adminDetails?.role_id?.permissions;

  // Check permissions for Consumer Banner
  const canViewConsumerBanner =
    canViewBanner(userPermissions) || hasFullBannerAccess(userPermissions);
  const canToggleConsumerBanner =
    canActivateDeactivateConsumerBanner(userPermissions) ||
    hasFullBannerAccess(userPermissions);
  const canDeleteConsumerBanner =
    canDeleteConsumerBannerUtil(userPermissions) ||
    hasFullBannerAccess(userPermissions);
  const canUploadConsumerBanner =
    canUploadConsumerBannerUtil(userPermissions) ||
    hasFullBannerAccess(userPermissions);
  const canDragConsumerBanner = canUploadConsumerBanner; // Only allow drag if user can upload

  // Check permissions for Client/Center Banner
  const canViewClientCenterBanner =
    canViewClientCenterBannerUtil(userPermissions) ||
    hasFullBannerAccess(userPermissions);
  const canToggleClientCenterBanner =
    canActivateDeactivateClientCenterBannerUtil(userPermissions) ||
    hasFullBannerAccess(userPermissions);
  const canDeleteClientCenterBanner =
    canDeleteClientCenterBannerUtil(userPermissions) ||
    hasFullBannerAccess(userPermissions);
  const canUploadClientCenterBanner =
    canUploadClientCenterBannerUtil(userPermissions) ||
    hasFullBannerAccess(userPermissions);

  const [activeTab, setActiveTab] = useState(0);
  const [banners, setBanners] = useState([]);
  const [clientCenterBanners, setClientCenterBanners] = useState([]);
  const [loading, setLoading] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [openModal, setOpenModal] = useState(false);
  const [searchParams, setSearchParams] = useState({
    clientId: "",
    centerId: "",
    qr_id: "",
    isActive: true,
  });

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Check if user has any banner access
  const hasAnyBannerAccess =
    canViewConsumerBanner ||
    canViewClientCenterBanner ||
    hasFullBannerAccess(userPermissions);

  const handleTabChange = (event, newValue) => {
    setActiveTab(newValue);
  };

  const handleImageClick = (image) => {
    setSelectedImage(image);
    setOpenModal(true);
  };

  const handleModalClose = () => {
    setOpenModal(false);
    setSelectedImage(null);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;

    if (over && active.id !== over.id && canDragConsumerBanner) {
      let updatedBanners;

      if (activeTab === 0) {
        // Consumer banners
        updatedBanners = arrayMove(
          banners,
          banners.findIndex((banner) => banner._id === active.id),
          banners.findIndex((banner) => banner._id === over.id)
        );
        setBanners(updatedBanners);

        try {
          const response = await axios.post(
            `${BASE_URL}admin/updateSequenceofBannerImage`,
            { bannerId: updatedBanners.map((banner) => banner._id) }
          );

          if (response.status === 200) {
            getAllBanner();
          }
        } catch (error) {
          console.error("Error updating sequence:", error);
        }
      }
      // Note: Client/Center banners might not need sequencing
    }
  };

  const getAllBanner = async () => {
    if (!canViewConsumerBanner) return;

    try {
      const response = await axios.get(`${BASE_URL}admin/getAllBannerImage`);
      setBanners(
        response?.data?.data?.sort((a, b) => a.sequenceOrder - b.sequenceOrder)
      );
    } catch (error) {
      console.error("Error fetching banners:", error);
    }
  };

  const getClientCenterBanners = async () => {
    if (!canViewClientCenterBanner) return;

    try {
      const params = new URLSearchParams();
      if (searchParams.clientId)
        params.append("clientId", searchParams.clientId);
      if (searchParams.centerId)
        params.append("centerId", searchParams.centerId);
      if (searchParams.qr_id) params.append("qr_id", searchParams.qr_id);
      params.append("isActive", searchParams.isActive);

      const response = await axios.get(
        `${BASE_URL}admin/get-common-banner?${params.toString()}`
      );
      setClientCenterBanners(response?.data?.data || []);
    } catch (error) {
      console.error("Error fetching client/center banners:", error);
    }
  };

  const uploadBanner = async (file, qrId, isActive, type) => {
    const formData = new FormData();

    if (type === "consumer") {
      if (!canUploadConsumerBanner) return;

      formData.append("bannerImage", file);

      const response = await axios.post(
        `${BASE_URL}admin/uploadBannerImage`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 200) {
        getAllBanner();
      }
    } else {
      if (!canUploadClientCenterBanner) return;

      formData.append("qr_id", qrId);
      formData.append("bannerImage", file);
      formData.append("isActive", isActive);

      const response = await axios.post(
        `${BASE_URL}admin/upload-banner-based-on-qr`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );

      if (response.status === 200) {
        getClientCenterBanners();
      }
    }
  };

  const toggleBannerStatus = async (bannerId, isActive) => {
    try {
      if (activeTab === 0) {
        if (!canToggleConsumerBanner) return;

        // Consumer banner
        const response = await axios.post(
          `${BASE_URL}admin/updateBannerImageStatus`,
          { bannerId, isActive }
        );

        if (response.status === 200) {
          setBanners((prevBanners) =>
            prevBanners.map((banner) =>
              banner._id === bannerId ? { ...banner, isActive } : banner
            )
          );
        }
      } else {
        if (!canToggleClientCenterBanner) return;

        // Client/Center banner
        const response = await axios.post(
          `${BASE_URL}admin/update-common-banner-status`,
          { bannerId, isActive }
        );

        if (response.status === 200) {
          setClientCenterBanners((prevBanners) =>
            prevBanners.map((banner) =>
              banner._id === bannerId ? { ...banner, isActive } : banner
            )
          );
        }
      }
    } catch (error) {
      console.error("Error updating banner status:", error);
    }
  };

  const deleteBanner = async (bannerId) => {
    try {
      if (activeTab === 0) {
        if (!canDeleteConsumerBanner) return;

        // Consumer banner
        const response = await axios.delete(
          `${BASE_URL}admin/deleteBannerImage/${bannerId}`
        );

        if (response.status === 200) {
          setBanners((prevBanners) =>
            prevBanners.filter((banner) => banner._id !== bannerId)
          );
        }
      } else {
        if (!canDeleteClientCenterBanner) return;

        // Client/Center banner
        const response = await axios.delete(
          `${BASE_URL}admin/delete-common-banner/${bannerId}`
        );

        if (response.status === 200) {
          setClientCenterBanners((prevBanners) =>
            prevBanners.filter((banner) => banner._id !== bannerId)
          );
        }
      }
    } catch (error) {
      console.error("Error deleting banner:", error);
    }
  };

  useEffect(() => {
    if (activeTab === 0 && canViewConsumerBanner) {
      getAllBanner();
    } else if (activeTab === 1 && canViewClientCenterBanner) {
      getClientCenterBanners();
    }
  }, [activeTab, searchParams]);

  // If user has no banner access at all
  if (!hasAnyBannerAccess) {
    return (
      <Box sx={{ p: 3, textAlign: "center" }}>
        <Typography variant="h6" color="error">
          You don't have permission to access Banner Management.
        </Typography>
      </Box>
    );
  }

  return (
    <>
      <AppBar position="static" color="default" elevation={1}>
        <Toolbar>
          <Typography variant="h4" sx={{ flexGrow: 1, fontWeight: 600 }}>
            Banner Management
          </Typography>
        </Toolbar>
      </AppBar>

      <Box sx={{ p: 3 }}>
        <Box sx={{ borderBottom: 1, borderColor: "divider", mb: 3 }}>
          <Tabs value={activeTab} onChange={handleTabChange}>
            {canViewConsumerBanner && <Tab label="Consumer Banners" />}
            {canViewClientCenterBanner && (
              <Tab label="Client & Center Banners" />
            )}
          </Tabs>
        </Box>

        {activeTab === 1 && canViewClientCenterBanner && (
          <Box sx={{ mb: 3, p: 2, bgcolor: "grey.50", borderRadius: 2 }}>
            <Typography variant="h6" gutterBottom>
              Filter Client/Center Banners
            </Typography>
            <Box sx={{ display: "flex", gap: 2, flexWrap: "wrap" }}>
              <TextField
                label="Client ID"
                value={searchParams.clientId}
                onChange={(e) =>
                  setSearchParams({ ...searchParams, clientId: e.target.value })
                }
                size="small"
              />
              <TextField
                label="Center ID"
                value={searchParams.centerId}
                onChange={(e) =>
                  setSearchParams({ ...searchParams, centerId: e.target.value })
                }
                size="small"
              />
              <TextField
                label="QR ID"
                value={searchParams.qr_id}
                onChange={(e) =>
                  setSearchParams({ ...searchParams, qr_id: e.target.value })
                }
                size="small"
                placeholder="INA/INC"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={searchParams.isActive}
                    onChange={(e) =>
                      setSearchParams({
                        ...searchParams,
                        isActive: e.target.checked,
                      })
                    }
                    color="primary"
                  />
                }
                label="Active Only"
              />
              <Button
                variant="contained"
                onClick={getClientCenterBanners}
                size="small"
                sx={{ alignSelf: "center" }}
              >
                Apply Filters
              </Button>
            </Box>
          </Box>
        )}

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 2,
            justifyContent: "flex-start",
            alignItems: "center",
          }}
        >
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={
                activeTab === 0
                  ? banners.map((banner) => banner._id)
                  : clientCenterBanners.map((banner) => banner._id)
              }
              strategy={verticalListSortingStrategy}
            >
              {(activeTab === 0 ? banners : clientCenterBanners).map(
                (banner) => (
                  <SortableItem
                    key={banner._id}
                    _id={banner._id}
                    banner={banner}
                    handleImageClick={handleImageClick}
                    toggleStatus={toggleBannerStatus}
                    deleteBanner={deleteBanner}
                    showQR={activeTab === 1}
                    canToggle={
                      activeTab === 0
                        ? canToggleConsumerBanner
                        : canToggleClientCenterBanner
                    }
                    canDelete={
                      activeTab === 0
                        ? canDeleteConsumerBanner
                        : canDeleteClientCenterBanner
                    }
                    canDrag={activeTab === 0 ? canDragConsumerBanner : false}
                  />
                )
              )}
            </SortableContext>
          </DndContext>

          {/* Upload Card - Show only if user has upload permission */}
          {(activeTab === 0 && canUploadConsumerBanner) ||
          (activeTab === 1 && canUploadClientCenterBanner) ? (
            <Card
              sx={{
                width: 300,
                height: 200,
                display: "flex",
                flexDirection: "column",
                justifyContent: "center",
                alignItems: "center",
                border: "2px dashed #ccc",
                cursor: "pointer",
                transition: "all 0.2s",
                borderRadius: "12px",
                "&:hover": {
                  transform: "scale(1.02)",
                  borderColor: "primary.main",
                  backgroundColor: "action.hover",
                },
              }}
              onClick={() => setUploadModalOpen(true)}
            >
              <IconButton>
                <AddCircleOutlineIcon
                  sx={{ fontSize: 48, color: "primary.main" }}
                />
              </IconButton>
              <Typography
                variant="body1"
                sx={{ color: "text.secondary", mt: 1 }}
              >
                Upload {activeTab === 0 ? "Consumer" : "Client/Center"} Banner
              </Typography>
            </Card>
          ) : null}
        </Box>

        <UploadBannerModal
          open={uploadModalOpen}
          onClose={() => setUploadModalOpen(false)}
          onUpload={uploadBanner}
          type={activeTab === 0 ? "consumer" : "client-center"}
          canUpload={
            activeTab === 0
              ? canUploadConsumerBanner
              : canUploadClientCenterBanner
          }
        />

        <Modal
          open={openModal}
          onClose={handleModalClose}
          aria-labelledby="banner-modal-title"
          aria-describedby="banner-modal-description"
        >
          <Box
            sx={{
              position: "absolute",
              top: "50%",
              left: "50%",
              transform: "translate(-50%, -50%)",
              bgcolor: "background.paper",
              boxShadow: 24,
              p: 2,
              borderRadius: 2,
              maxWidth: "90%",
              maxHeight: "90%",
              overflow: "auto",
              outline: "none",
            }}
          >
            <IconButton
              onClick={handleModalClose}
              sx={{
                position: "absolute",
                right: 8,
                top: 8,
                bgcolor: "background.paper",
              }}
            >
              <CloseIcon />
            </IconButton>
            {selectedImage ? (
              <img
                src={selectedImage}
                alt="Banner Preview"
                style={{
                  maxWidth: "100%",
                  maxHeight: "80vh",
                  display: "block",
                }}
              />
            ) : (
              <Typography variant="body2" color="textSecondary">
                No image selected
              </Typography>
            )}
          </Box>
        </Modal>
      </Box>
    </>
  );
};

export default BannerPage;
